import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { 
  BookOpen, 
  CheckCircle, 
  Circle, 
  ExternalLink, 
  User, 
  Calendar,
  Trophy,
  BarChart3,
  LogOut,
  Play,
  AlertCircle,
  Award,
  Download,
  Upload,
  Shield,
  Clock
} from 'lucide-react';
import Certificate from './Certificate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStage, setUpdatingStage] = useState(null);
  const [certificateStatus, setCertificateStatus] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  // ID Verification state
  const [idVerification, setIdVerification] = useState({
    status: 'not_required',
    uploaded_date: null,
    reviewed_date: null,
    rejection_reason: null,
    can_resubmit: true
  });
  const [uploadingId, setUploadingId] = useState(false);

  useEffect(() => {
    // Get student data from sessionStorage
    const studentData = sessionStorage.getItem('student_data');
    if (!studentData) {
      toast.error('Please log in to access your dashboard');
      navigate('/student-login');
      return;
    }

    const parsedStudent = JSON.parse(studentData);
    setStudent(parsedStudent);
    fetchProgress(parsedStudent.id);
    fetchCertificateStatus(parsedStudent.id);
    fetchIdVerificationStatus(parsedStudent.email);
  }, [navigate]);

  const fetchIdVerificationStatus = async (email) => {
    try {
      const response = await axios.get(`${API}/students/${email}/id-status`);
      setIdVerification(response.data);
    } catch (error) {
      console.error('Error fetching ID verification status:', error);
      // Keep default status if fetch fails
    }
  };

  const fetchProgress = async (studentId) => {
    try {
      const response = await axios.get(`${API}/student/${studentId}/progress`);
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
      // Don't show error for students who haven't paid for courses yet
      if (error.response?.status !== 403) {
        toast.error('Failed to load progress data');
      }
      // Set empty progress for students without course access
      setProgress({
        student_id: studentId,
        course_type: null,
        course_paid: false,
        foundation_progress: [],
        pro_progress: [],
        progress_percentage: 0,
        total_stages: 0,
        completed_stages: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificateStatus = async (studentId) => {
    try {
      const response = await axios.get(`${API}/student/${studentId}/certificate`);
      setCertificateStatus(response.data);
    } catch (error) {
      // Certificate not found or not ready
      setCertificateStatus(null);
    }
  };

  const requestCertificate = async () => {
    try {
      await axios.post(`${API}/student/${student.id}/request-certificate`, {
        student_id: student.id,
        course_type: student.course_type
      });
      toast.success('Certificate requested! Awaiting admin activation.');
      fetchCertificateStatus(student.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request certificate');
    }
  };

  const handleIdUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or PDF file only');
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingId(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/students/${student.email}/upload-id`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('ID document uploaded successfully! Awaiting review.');
      fetchIdVerificationStatus(student.email);
      
      // Clear the file input
      event.target.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload ID document');
    } finally {
      setUploadingId(false);
    }
  };

  const checkIdVerificationRequirement = async () => {
    try {
      await axios.post(`${API}/students/${student.email}/set-id-required`);
      fetchIdVerificationStatus(student.email);
    } catch (error) {
      console.error('Error setting ID verification requirement:', error);
    }
  };

  const toggleStageCompletion = async (stageNumber) => {
    if (!student || !progress) return;

    setUpdatingStage(stageNumber);
    try {
      const currentProgress = progress.course_type === 'foundation' 
        ? progress.foundation_progress 
        : progress.pro_progress;
      
      const isCurrentlyCompleted = currentProgress[stageNumber - 1];
      
      await axios.post(`${API}/student/${student.id}/progress`, {
        student_id: student.id,
        stage_number: stageNumber,
        completed: !isCurrentlyCompleted
      });

      // Refresh progress data
      await fetchProgress(student.id);
      
      toast.success(
        !isCurrentlyCompleted 
          ? `Stage ${stageNumber} marked as complete!` 
          : `Stage ${stageNumber} marked as incomplete`
      );
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    } finally {
      setUpdatingStage(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('student_data');
    navigate('/');
    toast.success('Logged out successfully');
  };

  const renderStageList = () => {
    if (!progress || !progress.course_type) return null;

    const currentProgress = progress.course_type === 'foundation' 
      ? progress.foundation_progress 
      : progress.pro_progress;
    
    const totalStages = progress.course_type === 'foundation' ? 5 : 9;
    const idVerificationRequired = student.course_paid && idVerification.status !== 'approved';

    if (idVerificationRequired) {
      return (
        <div className="text-center p-8 bg-slate-50 rounded-lg">
          <Shield className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-slate-700 mb-2">
            ID Verification Required
          </h4>
          <p className="text-slate-600">
            Please complete identity verification above to access course progress tracking.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {Array.from({ length: totalStages }, (_, index) => {
          const stageNumber = index + 1;
          const isCompleted = currentProgress[index];
          const isUpdating = updatingStage === stageNumber;

          return (
            <div
              key={stageNumber}
              className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${
                isCompleted 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => toggleStageCompletion(stageNumber)}
                  disabled={isUpdating}
                  className="flex-shrink-0 transition-colors duration-200"
                >
                  {isCompleted ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <Circle className="w-6 h-6 text-slate-400 hover:text-slate-600" />
                  )}
                </button>
                <div>
                  <h4 className={`font-medium ${
                    isCompleted ? 'text-green-800' : 'text-slate-800'
                  }`}>
                    Stage {stageNumber}
                  </h4>
                  <p className={`text-sm ${
                    isCompleted ? 'text-green-600' : 'text-slate-600'
                  }`}>
                    {progress.course_type === 'foundation' ? 'Foundation' : 'Pro'} Course Module
                  </p>
                </div>
              </div>
              
              {isUpdating && (
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!student || !progress) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-slate-800 mb-4">Unable to Load Dashboard</h2>
          <p className="text-slate-600 mb-6">Please try logging in again.</p>
          <Button onClick={() => navigate('/student-login')}>
            Back to Login
          </Button>
        </Card>
      </div>
    );
  }

  // Render different dashboard content based on student status
  const renderDashboardContent = () => {
    if (!student.enrollment_paid) {
      // Student hasn't paid enrollment fee
      return (
        <Card className="max-w-2xl mx-auto p-8 text-center">
          <AlertCircle className="w-16 h-16 text-orange-600 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Complete Your Enrollment</h2>
          <p className="text-slate-600 mb-6">
            To access your course dashboard and track your progress, please complete your enrollment payment.
          </p>
          <Button onClick={() => navigate('/enroll')} className="btn btn-primary">
            Complete Enrollment Payment
          </Button>
        </Card>
      );
    }
    
    if (!student.course_paid) {
      // Student paid enrollment but not course
      return (
        <div className="space-y-8">
          <Card className="p-6">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Enrollment Confirmed!</h2>
              <p className="text-slate-600 mb-6">
                Great! You've completed your enrollment payment. Now select your course to start tracking your progress.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <Card className="p-6 border-2 hover:border-blue-300 transition-colors">
                <h3 className="text-xl font-semibold text-blue-600 mb-2">Foundation Course</h3>
                <div className="text-3xl font-bold text-slate-800 mb-2">£75.00</div>
                <p className="text-slate-600 mb-4">5 comprehensive stages to master video book reviews</p>
                <ul className="text-sm text-slate-600 space-y-1 mb-6">
                  <li>• Stage 1: Introduction & Setup</li>
                  <li>• Stage 2: Review Techniques</li>
                  <li>• Stage 3: Video Production</li>
                  <li>• Stage 4: Content Strategy</li>
                  <li>• Stage 5: Monetization</li>
                </ul>
                <Button 
                  onClick={() => navigate('/enroll?step=course-selection&course=foundation')}
                  className="w-full"
                >
                  Select Foundation Course
                </Button>
              </Card>
              
              <Card className="p-6 border-2 hover:border-purple-300 transition-colors">
                <h3 className="text-xl font-semibold text-purple-600 mb-2">Pro Course</h3>
                <div className="text-3xl font-bold text-slate-800 mb-2">£215.00</div>
                <p className="text-slate-600 mb-4">9 advanced stages + everything from Foundation</p>
                <ul className="text-sm text-slate-600 space-y-1 mb-6">
                  <li>• Everything in Foundation</li>
                  <li>• 5 advanced modules</li>
                  <li>• Brand building masterclass</li>
                  <li>• Client Chatbot Training funnel creation</li>
                  <li>• Landing Page and ecommerce website</li>
                  <li>• Automated client systems</li>
                  <li>• Agency scaling strategies</li>
                  <li>• 6 video testimonials</li>
                </ul>
                <Button 
                  onClick={() => navigate('/enroll?step=course-selection&course=pro')}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Select Pro Course
                </Button>
              </Card>
            </div>
          </Card>
          
          {/* Show what they've paid so far */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Your Account Status</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-600">Enrollment Status</p>
                <p className="font-medium text-green-600">✅ Paid (£9.99)</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Course Selection</p>
                <p className="font-medium text-orange-600">⏳ Pending</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Progress Tracking</p>
                <p className="font-medium text-slate-400">🔒 Locked (Select Course)</p>
              </div>
            </div>
          </Card>
        </div>
      );
    }
    
    // Student has paid for course - show full dashboard
    return (
      <>
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Progress Overview */}
          <div className="lg:col-span-1">
            <Card className="p-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800">Course Progress</h3>
                <p className="text-slate-600 capitalize">{progress.course_type} Course</p>
              </div>

              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-800 mb-2">
                    {Math.round(progress.progress_percentage)}%
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${progress.progress_percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">
                    {progress.completed_stages} of {progress.total_stages} stages completed
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {progress.completed_stages}
                    </div>
                    <p className="text-xs text-slate-600">Completed</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-400">
                      {progress.total_stages - progress.completed_stages}
                    </div>
                    <p className="text-xs text-slate-600">Remaining</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* ID Verification Section */}
            {student.course_paid && (
              <Card className="p-6 mt-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-blue-600" />
                  Identity Verification
                </h3>
                
                {idVerification.status === 'not_required' && (
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-blue-700 mb-3">
                      Identity verification is required to access course materials. This ensures certificate integrity.
                    </p>
                    <Button 
                      onClick={checkIdVerificationRequirement}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Start ID Verification
                    </Button>
                  </div>
                )}

                {idVerification.status === 'required' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center mb-3">
                        <Upload className="w-5 h-5 text-amber-600 mr-2" />
                        <span className="font-medium text-amber-800">Upload Required Documents</span>
                      </div>
                      <p className="text-amber-700 text-sm mb-3">
                        Please upload a clear photo of your valid ID (driving license, passport, or government-issued ID card).
                      </p>
                      <p className="text-amber-700 text-sm mb-3">
                        Accepted formats: JPG, PNG, PDF (max 10MB)
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleIdUpload}
                        disabled={uploadingId}
                        className="block w-full text-sm text-slate-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
                      />
                    </div>
                    
                    {uploadingId && (
                      <div className="text-center p-2">
                        <div className="spinner inline-block mr-2"></div>
                        <span className="text-blue-600">Uploading...</span>
                      </div>
                    )}
                  </div>
                )}

                {idVerification.status === 'pending' && (
                  <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center justify-center mb-3">
                      <Clock className="w-5 h-5 text-yellow-600 mr-2" />
                      <span className="font-medium text-yellow-800">Under Review</span>
                    </div>
                    <p className="text-yellow-700 text-sm">
                      Your ID document has been uploaded and is being reviewed by our admin team. 
                      You'll be notified once verification is complete.
                    </p>
                    {idVerification.uploaded_date && (
                      <p className="text-yellow-600 text-xs mt-2">
                        Uploaded: {new Date(idVerification.uploaded_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {idVerification.status === 'rejected' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center mb-3">
                        <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                        <span className="font-medium text-red-800">Verification Rejected</span>
                      </div>
                      <p className="text-red-700 text-sm mb-2">
                        {idVerification.rejection_reason || 'Your ID document could not be verified.'}
                      </p>
                      <p className="text-red-700 text-sm">
                        Please upload a clear, valid ID document and try again.
                      </p>
                    </div>
                    
                    {idVerification.can_resubmit && (
                      <div>
                        <p className="text-slate-600 text-sm mb-3">Upload a new ID document:</p>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={handleIdUpload}
                          disabled={uploadingId}
                          className="block w-full text-sm text-slate-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-full file:border-0
                            file:text-sm file:font-semibold
                            file:bg-blue-50 file:text-blue-700
                            hover:file:bg-blue-100"
                        />
                        {uploadingId && (
                          <div className="text-center p-2 mt-2">
                            <div className="spinner inline-block mr-2"></div>
                            <span className="text-blue-600">Uploading...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {idVerification.status === 'approved' && (
                  <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-center mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      <span className="font-medium text-green-800">Verification Approved</span>
                    </div>
                    <p className="text-green-700 text-sm">
                      Your identity has been successfully verified. You can now access all course materials.
                    </p>
                    {idVerification.reviewed_date && (
                      <p className="text-green-600 text-xs mt-2">
                        Approved: {new Date(idVerification.reviewed_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Course Access */}
            {student.course_access_url && idVerification.status === 'approved' && (
              <Card className="p-6 mt-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                  <Play className="w-5 h-5 mr-2 text-blue-600" />
                  Course Access
                </h3>
                <Button 
                  className="w-full" 
                  onClick={() => window.open(student.course_access_url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Access Course Materials
                </Button>
              </Card>
            )}
          </div>

          {/* Course Stages */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <BarChart3 className="w-6 h-6 text-blue-600 mr-3" />
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800">
                      Course Stages
                    </h3>
                    <p className="text-slate-600">Track your progress through each stage</p>
                  </div>
                </div>
              </div>

              {renderStageList()}

              {progress.progress_percentage === 100 && (
                <>
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <Trophy className="w-6 h-6 text-green-600 mr-3" />
                      <div>
                        <h4 className="font-semibold text-green-800">
                          🎉 Congratulations! Course Completed!
                        </h4>
                        <p className="text-green-700 text-sm">
                          You've successfully completed all stages of the {progress.course_type} course.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Certificate Section */}
                  <Card className="mt-6 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Award className="w-6 h-6 text-blue-600 mr-3" />
                        <div>
                          <h4 className="font-semibold text-slate-800">Your Certificate</h4>
                          <p className="text-sm text-slate-600">
                            {certificateStatus?.status === 'ready' 
                              ? 'Your certificate is ready for download!'
                              : certificateStatus?.status === 'pending'
                              ? 'Certificate awaiting admin activation'
                              : 'Request your completion certificate'
                            }
                          </p>
                        </div>
                      </div>
                      <div>
                        {certificateStatus?.status === 'ready' ? (
                          <Button 
                            onClick={() => setShowCertificate(true)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            View Certificate
                          </Button>
                        ) : certificateStatus?.status === 'pending' ? (
                          <Button disabled className="bg-orange-100 text-orange-600">
                            Pending Activation
                          </Button>
                        ) : (
                          <Button onClick={requestCertificate}>
                            <Award className="w-4 h-4 mr-2" />
                            Request Certificate
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </Card>
          </div>
        </div>

        {/* Student Info */}
        <Card className="mt-8 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-blue-600" />
            Student Information
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-600">Name</p>
              <p className="font-medium text-slate-800">{student.first_name} {student.last_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Email</p>
              <p className="font-medium text-slate-800">{student.email}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Course Start Date</p>
              <p className="font-medium text-slate-800 flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {student.course_start_date || 'Not selected'}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Status</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                student.completion_status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : student.completion_status === 'in_progress'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {student.completion_status === 'completed' && '✅ '}
                {student.completion_status === 'in_progress' && '🔄 '}
                {student.completion_status === 'enrolled' && '📚 '}
                {student.completion_status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm text-slate-600">Your Referral Code</p>
              <div className="flex items-center space-x-2">
                <code className="font-mono font-bold text-slate-800 bg-slate-100 px-2 py-1 rounded">
                  {student.referral_code || 'Generating...'}
                </code>
                {student.referral_code && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(student.referral_code);
                      toast.success('Referral code copied!');
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm"
                    title="Copy referral code"
                  >
                    📋
                  </button>
                )}
              </div>
            </div>
          </div>
        </Card>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header with GIF */}
      <div className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/csgwpycc_train%20to%20become%20a%20video%20review%20specialist.gif"
          alt="Train to Become a Video Review Specialist"
          className="w-full h-48 md:h-64 lg:h-80 object-cover object-center"
        />
        <div className="bg-white py-6">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                  alt="Book Review Specialists Logo"
                  className="w-8 h-8 rounded-full object-cover mr-3"
                />
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
                    Welcome back, {student.first_name}!
                  </h1>
                  <p className="text-slate-600">Your Course Dashboard</p>
                </div>
              </div>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {renderDashboardContent()}
      </div>

      {/* Certificate Modal */}
      {showCertificate && certificateStatus?.status === 'ready' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-screen overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Your Certificate</h3>
              <Button 
                variant="outline" 
                onClick={() => setShowCertificate(false)}
              >
                Close
              </Button>
            </div>
            <div className="p-4">
              <Certificate 
                certificateData={certificateStatus.certificate}
                onDownload={() => {
                  toast.success('Certificate download started');
                  // Implement actual download logic
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;